import { useCallback, useEffect, useRef, type FocusEvent } from 'react'
import { isWorkingFocus } from '../logic/workingFocus'
import { useGlobalSidebarStore } from '../stores/useGlobalSidebarStore'

/**
 * Reports to the store whether the pointer or working focus is in a table
 * list's host (the rail's drawer, the Tables panel), so the list holds a
 * Recency re-sort meanwhile. Kept in refs, so moving in and out re-renders
 * nothing. `release` marks it unused when it closes without a pointer-leave.
 */
export function useDrawerInUse() {
  const pointerInsideRef = useRef(false)
  const focusInsideRef = useRef(false)
  const setDrawerInUse = useGlobalSidebarStore((s) => s.setDrawerInUse)
  const reportInUse = () => setDrawerInUse(pointerInsideRef.current || focusInsideRef.current)

  // Unmounted (a layout switch) with the pointer or focus inside, it leaves
  // without a pointer-leave or blur; it isn't in use any more.
  useEffect(
    () => () => {
      if (pointerInsideRef.current || focusInsideRef.current) {
        useGlobalSidebarStore.getState().setDrawerInUse(false)
      }
    },
    []
  )

  // Read when a key is pressed, so it never re-renders anything.
  const isPointerInside = useCallback(() => pointerInsideRef.current, [])

  const release = useCallback(() => {
    pointerInsideRef.current = false
    focusInsideRef.current = false
    setDrawerInUse(false)
  }, [setDrawerInUse])

  const handlers = {
    onPointerEnter: () => {
      pointerInsideRef.current = true
      reportInUse()
    },
    onPointerLeave: () => {
      pointerInsideRef.current = false
      reportInUse()
    },
    onFocus: (event: FocusEvent<HTMLElement>) => {
      focusInsideRef.current = isWorkingFocus(event.target)
      reportInUse()
    },
    onBlur: (event: FocusEvent<HTMLElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
        focusInsideRef.current = false
        reportInUse()
      }
    },
  }

  return { handlers, release, isPointerInside }
}
